import React from 'react';
import { Card, CardContent, CardHeader, Divider } from '@material-ui/core';
import useAsync from 'react-use/lib/useAsync';
import {
  DependencyGraph,
  DependencyGraphTypes,
  Progress,
} from '@backstage/core-components';
import {
  CodeownersModelInterface,
  useGithubStatisticsForTeam,
} from '../../hooks/useGithubStatistics';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';

export const TeamStructureCard = () => {
  const { entity } = useEntity();
  const ref = stringifyEntityRef({
    kind: entity.kind,
    namespace: entity.metadata.namespace,
    name: entity.metadata.name,
  });

  const fetchGithubStatisticsApi = useGithubStatisticsForTeam();

  const { value, loading } = useAsync(async () => {
    return fetchGithubStatisticsApi(ref);
  }, []);

  if (loading) {
    return <Progress />;
  }
  if (!value || Object.keys(value).length === 0) {
    return (
      <Card>
        <CardHeader title="Team structure" />
        <Divider />
        <CardContent>
          <p>
            No CODEOWNERS file detected in the GitHub repository, or it needs
            refreshing. Please consider creating or updating it.
          </p>
        </CardContent>
      </Card>
    );
  }

  const nodes: DependencyGraphTypes.DependencyNode[] = value.map(
    (codeowner: CodeownersModelInterface) => {
      return { id: codeowner.login, label: codeowner.login };
    },
  ) as { id: string; label: string }[];
  nodes.push({ id: ref, label: ref } as { id: string; label: string });

  const edges: DependencyGraphTypes.DependencyEdge[] = value
    .map((codeowner: CodeownersModelInterface) => ({
      from: codeowner.login,
      to: codeowner.team,
    }))
    .filter(edge => edge.to) as { from: string; to: string }[];

  edges.push(
    ...(value
      .map((codeowner: CodeownersModelInterface) => ({
        from: codeowner.team,
        to: ref,
      }))
      .filter(edge => edge.from) as { from: string; to: string }[]),
  );

  edges.push(
    ...(value
      .filter((codeowner: CodeownersModelInterface) => !codeowner.team)
      .map((codeowner: CodeownersModelInterface) => ({
        from: codeowner.login,
        to: ref,
      })) as { from: string; to: string }[]),
  );

  return (
    <Card>
      <CardHeader title="Team structure" />
      <Divider />
      <CardContent>
        <DependencyGraph nodes={nodes} edges={edges}></DependencyGraph>
      </CardContent>
    </Card>
  );
};
